import axios from "axios";
import filter from "lodash/filter";
import { ApiSetting } from "./ApiSettings";
import History from "../helpers/history";
import useAuthStore from "../stores/useAuthStore";
import useErrorStore from "../stores/useErrorStore";
import {
  getRefreshToken,
  refreshAccessToken,
  clearCredentials,
  revokeRefreshToken,
} from "./tokenStore";

let nextCancelToken = null;

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Queue of requests waiting for token refresh
let refreshSubscribers = [];

async function doRequest(
  method,
  path,
  {
    headers = {},
    data,
    onUploadProgress,
    cancelToken,
    responseType,
    returnResponseObj,
    // Opt out of the global "Action Failed" forbidden modal on a 403. The modal
    // is a side effect of this helper (it writes the error store), so a
    // caller that can legitimately hit a 403 in the background — e.g. a stale
    // permission firing a nav-list fetch — has no other way to suppress it; the
    // promise still rejects so the caller observes the failure.
    suppressForbiddenModal = false,
    _isRetry = false, // Internal flag to prevent infinite retry loops
  } = {}
) {
  const url = ApiSetting.DEFAULT_HOST + ApiSetting.DEFAULT_BASE_PATH + path;
  const userAuth = useAuthStore.getState().userAuth;
  const _tokenUsed = userAuth?.jwt || null;
  if (userAuth) {
    headers.Authorization = userAuth.jwt;
  }

  return doGenericRequest(method, url, {
    headers,
    data,
    onUploadProgress,
    cancelToken,
    responseType,
    returnResponseObj,
    suppressForbiddenModal,
    _isRetry,
    _tokenUsed,
    _originalPath: path,
    _originalMethod: method,
  });
}

/**
 * Make a request without authentication headers.
 * Used for refresh token and logout endpoints to avoid circular dependencies.
 */
async function doRequestWithoutAuth(
  method,
  path,
  { headers = {}, data, responseType, returnResponseObj } = {}
) {
  const url = ApiSetting.DEFAULT_HOST + ApiSetting.DEFAULT_BASE_PATH + path;

  return _doGenericRequestWithoutAuth(method, url, {
    headers,
    data,
    responseType,
    returnResponseObj,
  });
}

/**
 * Low-level request without auth - doesn't trigger refresh on 401
 */
async function _doGenericRequestWithoutAuth(
  method,
  url,
  { headers = {}, data, responseType, returnResponseObj } = {}
) {
  let reqOptions = {
    method,
    url,
    headers,
    responseType,
  };
  if (data) {
    reqOptions.headers["content-type"] = "application/json";
    reqOptions.data = data;
  }

  const response = await axios(reqOptions);
  return returnResponseObj ? response : response.data;
}

async function doGenericRequest(...args) {
  return new Promise((resolve, reject) => {
    _doGenericRequest(...args)
      .then(resolve)
      .catch(error => {
        if (error?.message === "Request Cancelled") {
          return; // Avoid displaying cancelled request error in console
        }
        reject(error);
      });
  });
}

async function _doGenericRequest(
  method,
  url,
  {
    headers = {},
    data,
    onUploadProgress,
    cancelToken,
    responseType,
    returnResponseObj,
    suppressForbiddenModal = false,
    _isRetry = false,
    _tokenUsed,
    _originalPath,
    _originalMethod,
  } = {}
) {
  if (nextCancelToken) {
    cancelToken = nextCancelToken.token;
    nextCancelToken = null;
  }
  let reqOptions = {
    method,
    url,
    headers,
    onUploadProgress,
    cancelToken,
    responseType,
  };
  if (data) {
    if (data instanceof FormData)
      reqOptions.headers["content-type"] = "multipart/form-data";
    else reqOptions.headers["content-type"] = "application/json";
    reqOptions.data = data;
  }
  let response;
  try {
    response = await axios(reqOptions);
    if (Math.floor(response.status / 100) !== 2) {
      // any 2xx response is valid
      // Note: Don't remove userAuth here - let the catch block handle 401 with refresh
      throw new Error(
        `API request failed! - ${response.status}: ${response.statusText}`
      );
    }
    return returnResponseObj ? response : response.data;
  } catch (error) {
    if (error.toString() === "Cancel") {
      error.message = "Request Cancelled";
      throw error; // Don't log errors for cancelled requests
    }

    error.response = error.response || response || {};
    if (error.response.hasOwnProperty("data") && error.response.data.message) {
      error.message = error.response.data.message;
    }

    if (error.response?.status === 401 && !_isRetry) {
      // Account deactivated: skip refresh, go straight to logout
      if (error.response?.data?.code === "ACCOUNT_DEACTIVATED") {
        if (useAuthStore.getState().userAuth) {
          await handleAuthFailure();
        }
        throw error;
      }

      // Check if the token has already been refreshed since this request was sent.
      // Late-arriving 401s (from slow requests) would otherwise trigger redundant refresh cycles.
      const currentToken = useAuthStore.getState().userAuth?.jwt;
      if (_tokenUsed && currentToken && _tokenUsed !== currentToken) {
        if (_originalPath) {
          return doRequest(_originalMethod, _originalPath, {
            headers: {},
            data,
            onUploadProgress,
            cancelToken,
            responseType,
            returnResponseObj,
            suppressForbiddenModal,
            _isRetry: true,
          });
        }
        throw error;
      }

      const refreshResult = await attemptTokenRefresh();
      if (refreshResult && _originalPath) {
        return doRequest(_originalMethod, _originalPath, {
          headers: {},
          data,
          onUploadProgress,
          cancelToken,
          responseType,
          returnResponseObj,
          suppressForbiddenModal,
          _isRetry: true,
        });
      }
      // Refresh failed - redirect to login
      await handleAuthFailure();
      throw error;
    }

    if (error.response.status === 403 && !suppressForbiddenModal) {
      storeForbiddenError(
        `${method} - ${url.split(ApiSetting.DEFAULT_BASE_PATH)[1]}`
      );
    }

    throw error;
  }
}

/**
 * Attempt to refresh the access token.
 * Returns true if successful, false otherwise.
 * Handles request queuing to prevent multiple simultaneous refresh attempts.
 */
async function attemptTokenRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    return new Promise(resolve => {
      refreshSubscribers.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    // Pass doRequestWithoutAuth to avoid circular dependency
    await refreshAccessToken(doRequestWithoutAuth);
    // Resolve all waiting subscribers, including any that arrive during notification
    while (refreshSubscribers.length > 0) {
      const subscriber = refreshSubscribers.shift();
      subscriber(true);
    }
    return true;
  } catch (error) {
    console.warn("Token refresh failed:", error);
    while (refreshSubscribers.length > 0) {
      const subscriber = refreshSubscribers.shift();
      subscriber(false);
    }
    return false;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Handle authentication failure by clearing credentials and redirecting to login.
 */
async function handleAuthFailure() {
  window.dispatchEvent(new Event("authFailed"));

  await revokeRefreshToken(doRequestWithoutAuth);
  await clearCredentials();
  window.location.href = "/";
}

function storeForbiddenError(item) {
  useErrorStore.getState().addForbiddenItem(item);
}

function storeError(message, items = []) {
  useErrorStore.getState().setError(message, items);
}

function applyFilters(filters, path) {
  if (filters) {
    let queryParams = Object.keys(filters).map(key => {
      const value = filters[key];
      if (value !== undefined && value !== null) {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      } else return null;
    });
    queryParams = filter(queryParams);

    const queryStr = queryParams.join("&").replace("+", "%2b");
    if (queryStr.length) path += `?${queryStr}`;
  }
  return path;
}

async function downloadFile(path, options = {}) {
  const { method = "GET", name, ...requestOpts } = options;
  let blob, contentDisposition;
  if (path instanceof Blob) {
    blob = path;
  } else {
    const response = await doRequest("GET", path, {
      responseType: "blob",
      returnResponseObj: true,
      ...requestOpts,
    });
    contentDisposition = response.headers["content-disposition"];
    blob = new Blob([response.data], { type: response.data.type });
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  let fileName = name || "unknown";
  if (!name && contentDisposition) {
    fileName = decodeURIComponent(
      /filename\*?=(?:[^']*'')?([^;]*)/.exec(contentDisposition)[1]
    );
  }
  link.setAttribute("download", fileName);
  link.click();
  URL.revokeObjectURL(url);
}

function attachCancelTokenToNextRequest() {
  const cancelToken = axios.CancelToken.source();
  nextCancelToken = cancelToken;
  return { ...cancelToken };
}

const wait = ms => new Promise(r => setTimeout(r, ms));

/**
 * Retry executing request until it resolves or timeout is reached
 * @param {Promise} request Request which might fail
 * @param {number} [timeout=5000] Timeout to allow for retries (in ms)
 * @param {number} [delay=1000] Delay between retries (in ms)
 */
const retryOnError = (request, timeout = 5000, delay = 1000, _start = 0) => {
  return new Promise((resolve, reject) => {
    _start = _start || new Date();
    const elapsed = new Date() - _start;
    return request()
      .then(resolve)
      .catch(reason => {
        if (elapsed < timeout) {
          return wait(delay)
            .then(retryOnError.bind(null, request, timeout, delay, _start))
            .then(resolve)
            .catch(reject);
        }
        return reject(reason);
      });
  });
};

export {
  doRequest,
  doRequestWithoutAuth,
  doGenericRequest,
  applyFilters,
  downloadFile,
  attachCancelTokenToNextRequest,
  retryOnError,
  storeForbiddenError,
  storeError,
};
